/**
 * Back in Stock Notification Form
 * 
 * Custom Element that manages the "Notify me when available" form
 * for out-of-stock product variants.
 * 
 * Features:
 * 1. Click listener on ALL variant swatch labels
 *    - Disabled swatch → show BIS form, visually select swatch, scroll to form
 *    - Enabled swatch → hide BIS form, remove visual selection, show buy buttons
 * 2. Document-level 'variant:changed' event (section rendering API)
 * 3. Smooth scroll + entrance animation for form visibility
 * 4. Visual swatch selection via .bis-selected CSS class
 */
if (!customElements.get('back-in-stock-form')) {
  customElements.define(
    'back-in-stock-form',
    class BackInStockForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.emailInput = this.querySelector('.back-in-stock__email');
        this.submitBtn = this.querySelector('.back-in-stock__submit');
        this.errorMessage = this.querySelector('.js-bis-error');
        this.successMessage = this.querySelector('.back-in-stock__message--success');
        this.sectionId = this.dataset.sectionId;
      }

      connectedCallback() {
        this._variantChangedHandler = this.handleVariantChanged.bind(this);
        document.addEventListener('variant:changed', this._variantChangedHandler);

        if (this.form) {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }

        // Defer so it runs AFTER newsletter modal code has executed
        setTimeout(() => this.handlePostSubmit(), 0);
        // Also run after full DOM load as extra safety net
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => setTimeout(() => this.handlePostSubmit(), 100));
        }

        this.attachSwatchListeners();
      }

      async handleSubmit(event) {
        event.preventDefault();

        const email = this.emailInput?.value;
        const variantId = this.dataset.variantId || this.form?.querySelector('input[name="variant_id"]')?.value;
        const variantTitle = this.dataset.variantTitle || this.form?.querySelector('input[name="variant_title"]')?.value;
        const productHandle = this.dataset.productHandle || this.form?.querySelector('input[name="product_handle"]')?.value;
        const productTitle = this.dataset.productTitle || this.form?.querySelector('input[name="product_title"]')?.value;

        if (!email || !variantId) {
          this.showError("Lütfen geçerli bir e-posta adresi girin.");
          return;
        }

        this.setLoading(true);
        this.resetMessages();

        try {
          const response = await fetch('https://bast-back-in-stock.raspy-morning-93f7.workers.dev/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: email,
              variantId: variantId,
              variantTitle: variantTitle,
              productHandle: productHandle,
              productTitle: productTitle
            })
          });

          const data = await response.json();

          if (!response.ok || data.error) {
            throw new Error(data.error || 'Bir hata oluştu, lütfen tekrar deneyin.');
          }

          this.showSuccess();
          if (this.emailInput) this.emailInput.value = '';

        } catch (error) {
          console.error('BIS Submission Error:', error);
          this.showError(error.message);
        } finally {
          this.setLoading(false);
        }
      }

      /**
       * After form submit, Shopify adds ?customer_posted=true to URL.
       * This triggers the newsletter "already subscribed" popup on every reload.
       * We clean the URL and close the newsletter modal.
       */
      handlePostSubmit() {
        const url = new URL(window.location.href);
        const customerPosted = url.searchParams.get('customer_posted');

        if (customerPosted === 'true') {
          // Clean URL params to prevent popup on future reloads
          url.searchParams.delete('customer_posted');
          url.searchParams.delete('form_type');
          window.history.replaceState({}, '', url.toString());

          // Close/hide Nexvo newsletter modal if it opened
          document.querySelectorAll('.modal--newsletter-alert').forEach(modal => {
            modal.hide?.();
            modal.setAttribute('hidden', '');
          });

          // Remove modal overlays that block scroll
          document.querySelectorAll('.fixed-overlay').forEach(overlay => {
            overlay.remove();
          });

          // Restore body scroll - remove Nexvo's exact modal lock classes
          document.body.classList.remove('modal-show', 'modal-showing');
          document.body.style.overflow = '';
          document.documentElement.style.overflow = '';

          // Hide any newsletter success/error messages that Liquid rendered
          document.querySelectorAll('[id*="Newsletter-success"], [id*="Newsletter-error"]').forEach(el => {
            el.classList.add('hidden');
            el.style.display = 'none';
          });

          // Show BIS success message if this was a BIS submission
          const isBisSubmit = url.hash.includes('BackInStock');
          if (isBisSubmit) {
            this.show();
            const successEl = this.querySelector('.back-in-stock__message--success');
            if (successEl) successEl.classList.remove('hidden');
          }
        }
      }

      disconnectedCallback() {
        document.removeEventListener('variant:changed', this._variantChangedHandler);
      }

      getProductInfoContainer() {
        return this.closest('product-info') || this.closest('.product-info');
      }

      getBuyButtonsContainer() {
        const productInfo = this.getProductInfoContainer();
        if (productInfo) {
          return productInfo.querySelector('.product-form__buttons');
        }
        return this.parentElement?.querySelector('.product-form__buttons');
      }

      /**
       * Find the variant-selects container for this product
       */
      getVariantSelects() {
        const productInfo = this.getProductInfoContainer();
        return productInfo?.querySelector('variant-selects');
      }

      /**
       * Clear all .bis-selected classes from variant inputs
       */
      clearBisSelection() {
        const variantSelects = this.getVariantSelects();
        if (!variantSelects) return;
        variantSelects.querySelectorAll('label.bis-selected').forEach(label => {
          label.classList.remove('bis-selected');
        });
      }

      /**
       * Attach click listeners to ALL variant swatch labels.
       */
      attachSwatchListeners() {
        const productInfo = this.getProductInfoContainer();
        if (!productInfo) return;

        const variantSelects = productInfo.querySelector('variant-selects');
        if (!variantSelects) return;

        variantSelects.addEventListener('click', (event) => {
          const label = event.target.closest('label');
          if (!label) return;

          let input = null;
          const forId = label.getAttribute('for');
          if (forId) {
            input = document.getElementById(forId);
          }
          if (!input) {
            input = label.previousElementSibling;
          }
          if (!input || input.tagName !== 'INPUT') return;

          const buyButtons = this.getBuyButtonsContainer();

          if (input.disabled || input.classList.contains('disabled')) {
            // SOLD OUT swatch clicked

            // 1. Clear previous BIS selection
            this.clearBisSelection();
            label.classList.add('bis-selected');

            // 2. Temporarily enable, check, and dispatch change event
            //    This triggers the theme's native variant→image switch
            const wasDisabled = input.disabled;
            const hadDisabledClass = input.classList.contains('disabled');
            input.disabled = false;
            if (hadDisabledClass) input.classList.remove('disabled');
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));

            // 3. Re-disable after a tick so the theme processes the change
            requestAnimationFrame(() => {
              input.disabled = wasDisabled;
              if (hadDisabledClass) input.classList.add('disabled');
              // Re-add bis-selected since the class may have been removed during re-render
              label.classList.add('bis-selected');
            });

            // 4. Show BIS form + hide buy buttons
            this.show();
            if (buyButtons) buyButtons.classList.add('hidden');

            // 5. Update variant info in BIS form
            const variantTitleInput = this.form?.querySelector('input[name="variant_title"]');
            if (variantTitleInput) variantTitleInput.value = input.value;
            this.dataset.variantTitle = input.value;
            this.resetMessages();

            // 6. Scroll to form with smooth animation
            this.scrollToForm();
          } else {
            // IN STOCK swatch clicked
            this.clearBisSelection();
            this.hide();
            if (buyButtons) buyButtons.classList.remove('hidden');
          }
        });
      }

      /**
       * Scroll the BIS form into view with animation
       */
      scrollToForm() {
        // Remove anim class to allow re-trigger
        this.classList.remove('bis-animate');

        // Force reflow then add animation
        requestAnimationFrame(() => {
          this.classList.add('bis-animate');

          // Smooth scroll to form
          setTimeout(() => {
            this.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Focus email input after scroll completes
            setTimeout(() => {
              this.emailInput?.focus();
            }, 500);
          }, 100);
        });
      }

      handleVariantChanged(event) {
        const variant = event.detail?.variant;
        const buyButtons = this.getBuyButtonsContainer();

        if (!variant || !variant.available) {
          this.show();
          if (buyButtons) buyButtons.classList.add('hidden');
          if (variant) this.updateVariantInfo(variant);
          this.scrollToForm();
        } else {
          this.clearBisSelection();
          this.hide();
          if (buyButtons) buyButtons.classList.remove('hidden');
        }

        this.resetMessages();
      }

      updateVariantInfo(variant) {
        this.dataset.variantId = variant.id;
        this.dataset.variantTitle = variant.title || '';

        // Update hidden contact form fields when variant changes
        const variantInput = this.form?.querySelector('input[name="variant_title"]');
        const variantIdInput = this.form?.querySelector('input[name="variant_id"]');

        if (variantInput) variantInput.value = variant.title || '';
        if (variantIdInput) variantIdInput.value = variant.id;
      }

      show() {
        this.classList.remove('hidden');
      }

      hide() {
        this.classList.add('hidden');
        this.classList.remove('bis-animate');
      }

      setLoading(loading) {
        if (loading) {
          this.submitBtn?.classList.add('btn--loading');
          this.submitBtn?.setAttribute('disabled', 'disabled');
        } else {
          this.submitBtn?.classList.remove('btn--loading');
          this.submitBtn?.removeAttribute('disabled');
        }
      }

      showSuccess() {
        this.form?.classList.add('hidden');
        this.successMessage?.classList.remove('hidden');
        this.errorMessage?.classList.add('hidden');
      }

      showError(message) {
        const errorText = this.errorMessage?.querySelector('.back-in-stock__message-text');
        if (errorText) errorText.textContent = message;
        this.errorMessage?.classList.remove('hidden');
        this.successMessage?.classList.add('hidden');
      }

      resetMessages() {
        this.form?.classList.remove('hidden');
        this.successMessage?.classList.add('hidden');
        this.errorMessage?.classList.add('hidden');
      }
    }
  );
}
